import { useEffect, useState } from "react";
import { Button, Card } from "../../design-v2";
import SegmentedSelector from "./SegmentedSelector";
import SettingsSection from "./SettingsSection";
import SettingsValueRow from "./SettingsValueRow";
import { updateUserProfile } from "../../services/api";
import ConnectedAccountsSection from "./accountSections/ConnectedAccountsSection";
import AccountActionsSection from "./accountSections/AccountActionsSection";
import ProfileAvatarSection from "./accountSections/ProfileAvatarSection";

const SEX_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];

function formatSex(value) {
  if (value === "MALE") {
    return "Male";
  }
  if (value === "FEMALE") {
    return "Female";
  }
  // `null` reaches the row as an absent value, which renders the shared
  // `Not set` placeholder rather than a second spelling of the same idea.
  return null;
}

export default function AccountProfileScreen({
  profile = {},
  connectedAccounts = {},
  auth = {},
  capabilities = {},
  onAccountChange,
  onRefreshSettings,
  onProfileChange,
}) {
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const status = profile.demographicsStatus || "NOT_COLLECTED";
  const numericAge = Number(age);
  const isAgeValid =
    age.trim() !== "" &&
    Number.isInteger(numericAge) &&
    numericAge >= 18 &&
    numericAge <= 100;
  const isSexValid = sex === "MALE" || sex === "FEMALE";
  const isFormValid = isAgeValid && isSexValid;

  useEffect(() => {
    if (status === "NOT_COLLECTED") {
      setAge("");
      setSex("");
      setFieldErrors({});
      setSaveError("");
    }
  }, [status]);

  function validate() {
    const nextErrors = {};

    if (!isAgeValid) {
      nextErrors.age = "Age must be an integer from 18 through 100.";
    }
    if (!isSexValid) {
      nextErrors.sex = "Select Male or Female.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0 ? numericAge : null;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const numericAge = validate();
    if (numericAge == null) {
      return;
    }

    setIsSaving(true);
    setSaveError("");
    try {
      const response = await updateUserProfile({ age: numericAge, sex });
      onProfileChange?.({
        ...profile,
        ...response.profile,
      });
    } catch (error) {
      const nextFieldErrors = {};
      (Array.isArray(error?.details) ? error.details : []).forEach((issue) => {
        if (issue?.path === "age" || issue?.path === "sex") {
          nextFieldErrors[issue.path] = issue.message;
        }
      });
      setFieldErrors(nextFieldErrors);
      setSaveError(error?.message || "Unable to save profile information.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    /*
     * Three deliberately separated groups — Profile, Connected Accounts and
     * Account — instead of one undifferentiated card. Each group carries its
     * own label and its own surface, so the destructive control is easy to find
     * without competing with profile information.
     */
    <div className="lz-settings-account-screen">
      <SettingsSection title="Profile">
        <Card padding="none">
          <ProfileAvatarSection
            avatar={profile.avatar}
            sex={profile.sex}
            canUploadAvatar={capabilities.canUploadAvatar !== false}
            onAccountChange={onAccountChange}
          />

          {/*
            * Name, Email, Username, Age and Sex are informational on this
            * screen. They are rendered as inert `[label] [value]` rows: no
            * chevron, no button, no tap target — nothing that would promise an
            * edit this screen cannot perform.
            */}
          <dl className="lz-settings-value-list">
            <SettingsValueRow label="Name" value={profile.name} />
            <SettingsValueRow label="Email" value={profile.email} />
            <SettingsValueRow label="Username" value={profile.username} />
            {status === "LOCKED" ? (
              <>
                <SettingsValueRow label="Age" value={profile.currentAge} />
                <SettingsValueRow label="Sex" value={formatSex(profile.sex)} />
              </>
            ) : null}
          </dl>
        </Card>
      </SettingsSection>

      {status !== "LOCKED" ? (
        <SettingsSection title="Personalization">
          {status === "NOT_COLLECTED" ? (
            <Card padding="md">
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1">
                  <p className="lz-settings-muted-text">
                    Used to personalize AI-generated programs.
                  </p>
                  <p className="lz-settings-muted-text">
                    These values can’t currently be changed after saving.
                  </p>
                </div>

                <div>
                  <label htmlFor="profile-age" className="lz-settings-label">
                    Age
                  </label>
                  <input
                    id="profile-age"
                    type="number"
                    min="18"
                    max="100"
                    step="1"
                    value={age}
                    onChange={(event) => {
                      setAge(event.target.value);
                      setFieldErrors((current) => ({ ...current, age: undefined }));
                    }}
                    className="lz-settings-input mt-2"
                    aria-describedby={fieldErrors.age ? "profile-age-error" : undefined}
                    aria-invalid={fieldErrors.age ? "true" : undefined}
                    disabled={isSaving}
                  />
                  {fieldErrors.age ? (
                    <p id="profile-age-error" className="lz-settings-error">
                      {fieldErrors.age}
                    </p>
                  ) : null}
                </div>

                <div>
                  <p className="lz-settings-label mb-2">Sex</p>
                  <SegmentedSelector
                    label="Sex"
                    options={SEX_OPTIONS}
                    value={sex}
                    onChange={(value) => {
                      setSex(value);
                      setFieldErrors((current) => ({ ...current, sex: undefined }));
                    }}
                  />
                  {fieldErrors.sex ? (
                    <p className="lz-settings-error">{fieldErrors.sex}</p>
                  ) : null}
                </div>

                {saveError ? (
                  <p className="lz-settings-error" role="alert">
                    {saveError}
                  </p>
                ) : null}

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSaving || !isFormValid}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            </Card>
          ) : status === "INCONSISTENT" ? (
            <Card padding="none">
              <p className="lz-settings-value-list__note">
                Some profile information is incomplete and cannot currently be changed.
              </p>
              <dl className="lz-settings-value-list">
                <SettingsValueRow label="Age" value={profile.age} />
                <SettingsValueRow label="Sex" value={formatSex(profile.sex)} />
              </dl>
            </Card>
          ) : null}
        </SettingsSection>
      ) : null}

      <ConnectedAccountsSection
        providers={Array.isArray(connectedAccounts.providers)
          ? connectedAccounts.providers
          : []}
        auth={auth}
        capabilities={capabilities}
        onAccountChange={onAccountChange}
        onRefreshSettings={onRefreshSettings}
      />
      <AccountActionsSection />
    </div>
  );
}
